import React from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Text,
  Tooltip,
  Switch,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import {
  MdNearMe,
  MdCrop,
  MdCropSquare,
  MdArrowRightAlt,
  MdTitle,
  MdBlurOn,
  MdGesture,
  MdUndo,
  MdRedo,
  MdFileDownload,
  MdContentCopy,
} from 'react-icons/md';

const COLORS = ['#ef4444', '#eab308', '#2563eb'];

export const TOOLS = {
  select: { id: 'select', label: 'Select', short: 'V', icon: MdNearMe },
  crop: { id: 'crop', label: 'Crop', short: 'C', icon: MdCrop },
  rect: { id: 'rect', label: 'Rectangle', short: 'R', icon: MdCropSquare },
  arrow: { id: 'arrow', label: 'Arrow', short: 'A', icon: MdArrowRightAlt },
  text: { id: 'text', label: 'Text', short: 'T', icon: MdTitle },
  blur: { id: 'blur', label: 'Blur', short: 'B', icon: MdBlurOn },
  pen: { id: 'pen', label: 'Draw', short: 'P', icon: MdGesture },
};

const STROKE_WIDTHS = [2, 4, 8];

const ToolBtn = ({ active, toolKey, onPick, isDisabled }) => {
  const t = TOOLS[toolKey];
  const Icon = t.icon;
  return (
    <Tooltip label={`${t.label} (${t.short})`} placement="top" hasArrow openDelay={400}>
      <IconButton
        aria-label={t.label}
        icon={<Icon size={20} />}
        size="sm"
        variant={active ? 'solid' : 'ghost'}
        colorScheme={active ? 'blue' : 'gray'}
        onClick={() => onPick(t.id)}
        isDisabled={isDisabled}
        borderRadius="lg"
      />
    </Tooltip>
  );
};

const AnnotatorToolbar = ({
  tool,
  onTool,
  color,
  onColor,
  strokeWidth,
  onStrokeWidth,
  quickBlur,
  onQuickBlur,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onDownload,
  onCopy,
  hasImage,
  copyDisabledReason,
}) => {
  return (
    <Box
      position="fixed"
      bottom={{ base: 3, md: 6 }}
      left="50%"
      transform="translateX(-50%)"
      zIndex={50}
      px={3}
      py={2.5}
      bg="rgba(255,255,255,0.92)"
      backdropFilter="blur(12px)"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="2xl"
      boxShadow="0 8px 30px rgba(15, 23, 42, 0.08)"
      maxW="calc(100vw - 24px)"
    >
      <Flex
        align="center"
        gap={{ base: 2, md: 3 }}
        flexWrap="wrap"
        justify="center"
      >
        <HStack spacing={1} pr={{ base: 0, md: 2 }} borderRightWidth={{ md: '1px' }} borderColor="gray.200">
          <ToolBtn toolKey="select" active={tool === 'select'} onPick={onTool} isDisabled={!hasImage} />
          <ToolBtn toolKey="crop" active={tool === 'crop'} onPick={onTool} isDisabled={!hasImage} />
          <ToolBtn toolKey="rect" active={tool === 'rect'} onPick={onTool} isDisabled={!hasImage} />
          <ToolBtn toolKey="arrow" active={tool === 'arrow'} onPick={onTool} isDisabled={!hasImage} />
          <ToolBtn toolKey="text" active={tool === 'text'} onPick={onTool} isDisabled={!hasImage} />
          <ToolBtn toolKey="blur" active={tool === 'blur'} onPick={onTool} isDisabled={!hasImage} />
          <ToolBtn toolKey="pen" active={tool === 'pen'} onPick={onTool} isDisabled={!hasImage} />
        </HStack>

        <HStack spacing={1.5}>
          {COLORS.map((c) => (
            <Tooltip key={c} label="Stroke / text color" placement="top" openDelay={300}>
              <Box
                as="button"
                type="button"
                disabled={!hasImage}
                w="26px"
                h="26px"
                borderRadius="full"
                bg={c}
                borderWidth="2px"
                borderColor={color === c ? 'gray.800' : 'transparent'}
                boxShadow={color === c ? '0 0 0 1px rgba(0,0,0,0.08)' : 'none'}
                onClick={() => hasImage && onColor(c)}
                aria-label={`Color ${c}`}
                _hover={hasImage ? { transform: 'scale(1.06)' } : undefined}
                transition="transform 0.12s"
                opacity={hasImage ? 1 : 0.45}
                cursor={hasImage ? 'pointer' : 'not-allowed'}
              />
            </Tooltip>
          ))}
        </HStack>

        <HStack spacing={1}>
          {STROKE_WIDTHS.map((w) => (
            <Button
              key={w}
              size="xs"
              variant={strokeWidth === w ? 'solid' : 'ghost'}
              colorScheme="gray"
              borderRadius="md"
              minW="36px"
              onClick={() => onStrokeWidth(w)}
              isDisabled={!hasImage}
            >
              {w}px
            </Button>
          ))}
        </HStack>

        <FormControl
          display="flex"
          alignItems="center"
          w="auto"
          mx={{ base: 0, md: 1 }}
          isDisabled={!hasImage}
        >
          <FormLabel htmlFor="quick-blur" mb={0} fontSize="xs" color="gray.600" whiteSpace="nowrap" mr={2}>
            Quick blur
          </FormLabel>
          <Switch
            id="quick-blur"
            size="sm"
            isChecked={quickBlur}
            onChange={(e) => onQuickBlur(e.target.checked)}
          />
        </FormControl>

        <HStack spacing={1} pl={{ base: 0, md: 2 }} borderLeftWidth={{ md: '1px' }} borderColor="gray.200">
          <Tooltip label="Undo (⌘Z)" placement="top" openDelay={300}>
            <IconButton
              aria-label="Undo"
              icon={<MdUndo size={20} />}
              size="sm"
              variant="ghost"
              onClick={onUndo}
              isDisabled={!canUndo || !hasImage}
              borderRadius="lg"
            />
          </Tooltip>
          <Tooltip label="Redo (⌘⇧Z)" placement="top" openDelay={300}>
            <IconButton
              aria-label="Redo"
              icon={<MdRedo size={20} />}
              size="sm"
              variant="ghost"
              onClick={onRedo}
              isDisabled={!canRedo || !hasImage}
              borderRadius="lg"
            />
          </Tooltip>
        </HStack>

        <HStack spacing={1}>
          <Button
            size="sm"
            leftIcon={<MdFileDownload />}
            colorScheme="blue"
            variant="solid"
            borderRadius="lg"
            onClick={onDownload}
            isDisabled={!hasImage}
          >
            PNG
          </Button>
          <Tooltip label={copyDisabledReason || 'Copy image'} placement="top" openDelay={300}>
            <Button
              size="sm"
              leftIcon={<MdContentCopy />}
              variant="outline"
              borderRadius="lg"
              onClick={onCopy}
              isDisabled={!hasImage}
            >
              Copy
            </Button>
          </Tooltip>
        </HStack>

        <Text fontSize="10px" color="gray.400" display={{ base: 'none', lg: 'block' }} whiteSpace="nowrap">
          Paste · Drop · Client-side
        </Text>
      </Flex>
    </Box>
  );
};

export default AnnotatorToolbar;
